import {isFeatureEnabled} from '@github-ui/feature-flags'
import React, {useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore} from 'react'

import type Artwork from '../../webgl/Artwork'
import {
  type BrandedDividerRegistration,
  type DitherRegistration,
  type EnterpriseHeroRegistration,
  WebGLArtworkContext,
  WebGLContext,
  WebGLStatusContext,
} from '../WebGLContext/WebGLContext'

interface WebGLPageProps {
  children: React.ReactNode
  fallback?: React.ReactNode
  mode?: 'light' | 'dark'
}

function getWebGLContext(canvas: HTMLCanvasElement) {
  try {
    const contextAttributes: WebGLContextAttributes = {alpha: true}
    return canvas.getContext('webgl2', contextAttributes) || canvas.getContext('webgl', contextAttributes)
  } catch {
    return null
  }
}

export function WebGLPage({children, fallback = null, mode = 'light'}: WebGLPageProps) {
  const stableCanvasPosition = isFeatureEnabled('site_landing_pages_stable_canvas_position')
  const [dithers, setDithers] = useState<DitherRegistration[]>([])
  const [brandeddividers, setBrandedDividers] = useState<BrandedDividerRegistration[]>([])
  const [isWebGLUnavailable, setIsWebGLUnavailable] = useState(false)
  const registrationsRef = useRef<{
    dithers: DitherRegistration[]
    brandedDividers: BrandedDividerRegistration[]
    enterpriseHero: EnterpriseHeroRegistration | null
  }>({dithers: [], brandedDividers: [], enterpriseHero: null})
  const artworkStoreRef = useRef({artwork: null as Artwork | null, listeners: new Set<() => void>()})

  const subscribeToArtwork = useCallback((listener: () => void) => {
    artworkStoreRef.current.listeners.add(listener)
    return () => artworkStoreRef.current.listeners.delete(listener)
  }, [])

  const artwork = useSyncExternalStore(
    subscribeToArtwork,
    () => artworkStoreRef.current.artwork,
    () => null,
  )

  const registerDither = useCallback((registration: DitherRegistration) => {
    setDithers(prev => {
      // Check if already registered
      if (prev.find(d => d.id === registration.id)) return prev

      return [...prev, registration]
    })
  }, [])

  const unregisterDither = useCallback((id: string) => {
    setDithers(prev => {
      return prev.filter(dither => dither.id !== id)
    })
  }, [])

  const registerBrandedDivider = useCallback((registration: BrandedDividerRegistration) => {
    setBrandedDividers(prev => {
      // Check if already registered
      if (prev.some(s => s.id === registration.id)) return prev
      return [...prev, registration]
    })
  }, [])

  const unregisterBrandedDivider = useCallback((id: string) => {
    setBrandedDividers(prev => {
      return prev.filter(brandeddivider => brandeddivider.id !== id)
    })
  }, [])

  const registerEnterpriseHero = useCallback((registration: EnterpriseHeroRegistration) => {
    if (registrationsRef.current.enterpriseHero) return

    registrationsRef.current.enterpriseHero = registration
    artworkRef.current?.updateRegisterEnterpriseHero(registration)
  }, [])

  const unregisterEnterpriseHero = useCallback((id: string) => {
    if (registrationsRef.current.enterpriseHero?.id !== id) return

    registrationsRef.current.enterpriseHero = null
    artworkRef.current?.unregisterEnterpriseHero()
  }, [])

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const artworkRef = useRef<Artwork | null>(null)
  const rAFRef = useRef<number | null>(null)

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current
      let isCancelled = false

      const publishArtwork = (nextArtwork: Artwork | null) => {
        artworkStoreRef.current.artwork = nextArtwork
        for (const listener of artworkStoreRef.current.listeners) {
          listener()
        }
      }

      const handleResize = () => {
        if (artworkRef.current && canvasRef.current) {
          const width = document.body.clientWidth
          const height = window.innerHeight
          artworkRef.current.resize(width, height)
        }
      }

      const handleRaf = () => {
        artworkRef.current?.update()
        rAFRef.current = requestAnimationFrame(handleRaf)
      }

      const handleContextLost = () => {
        if (rAFRef.current) {
          cancelAnimationFrame(rAFRef.current)
          rAFRef.current = null
        }
        artworkRef.current = null
        publishArtwork(null)
        setIsWebGLUnavailable(true)
      }

      const handleSetupFailure = () => {
        if (!isCancelled) setIsWebGLUnavailable(true)
      }

      const setup = async () => {
        const context = getWebGLContext(canvas)
        if (!context) {
          handleSetupFailure()
          return
        }

        try {
          const {default: Artwork} = await import('../../webgl/Artwork')
          if (isCancelled) return
          const nextArtwork = new Artwork({canvas, context, stableCanvasPosition})
          artworkRef.current = nextArtwork
          publishArtwork(nextArtwork)

          if (mode === 'light') {
            nextArtwork.switchBackgroundColor({isWhite: true})
          } else {
            nextArtwork.switchBackgroundColor({isWhite: false})
          }

          nextArtwork.updateRegisterDither(registrationsRef.current.dithers)
          nextArtwork.updateRegisterBrandedDivider(registrationsRef.current.brandedDividers)
          if (registrationsRef.current.enterpriseHero) {
            nextArtwork.updateRegisterEnterpriseHero(registrationsRef.current.enterpriseHero)
          }

          handleRaf()
          handleResize()

          void (async () => {
            await document.fonts?.ready
            handleResize()
          })()
        } catch {
          handleSetupFailure()
        }
      }
      void setup()

      window.addEventListener('resize', handleResize)
      canvas.addEventListener('webglcontextlost', handleContextLost)

      return () => {
        isCancelled = true
        artworkRef.current = null
        publishArtwork(null)
        window.removeEventListener('resize', handleResize)
        canvas.removeEventListener('webglcontextlost', handleContextLost)
        if (rAFRef.current) {
          cancelAnimationFrame(rAFRef.current)
        }
      }
    }
    // Mount-only by design: adding `mode` would tear down and recreate the
    // whole artwork whenever it changes. `mode` is only read to seed the
    // initial background colour, so changing it after mount has no effect.
    // The canvas positioning experiment is also selected at mount; reload to switch it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    registrationsRef.current.dithers = dithers
    if (artworkRef.current) {
      artworkRef.current.updateRegisterDither(dithers)
    }
  }, [dithers])

  useEffect(() => {
    registrationsRef.current.brandedDividers = brandeddividers
    if (artworkRef.current) {
      artworkRef.current.updateRegisterBrandedDivider(brandeddividers)
    }
  }, [brandeddividers])

  const contextValue = useMemo(
    () => ({
      registerDither,
      unregisterDither,
      registerBrandedDivider,
      unregisterBrandedDivider,
      registerEnterpriseHero,
      unregisterEnterpriseHero,
    }),
    [
      registerDither,
      unregisterDither,
      registerBrandedDivider,
      unregisterBrandedDivider,
      registerEnterpriseHero,
      unregisterEnterpriseHero,
    ],
  )
  const webGLStatus = isWebGLUnavailable ? 'unavailable' : artwork ? 'available' : 'loading'

  return (
    <WebGLContext value={contextValue}>
      <WebGLStatusContext value={webGLStatus}>
        <WebGLArtworkContext value={artwork}>
          <div
            style={{
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {isWebGLUnavailable ? (
              fallback
            ) : (
              <canvas
                ref={canvasRef}
                style={{
                  pointerEvents: 'none',
                  position: 'absolute',
                  left: 0,
                  top: stableCanvasPosition ? 0 : undefined,
                  zIndex: 0,
                }}
                aria-hidden="true"
                tabIndex={-1}
              />
            )}
            {children}
          </div>
        </WebGLArtworkContext>
      </WebGLStatusContext>
    </WebGLContext>
  )
}
